import { useCallback, useEffect, useState } from 'react';
import {
  api,
  type Company,
  type FiscalYear,
  type StatementLine,
  type Statements,
  type PreviousYear,
  type StatutoryRow,
  type StatutoryStatement,
} from '../api';
import type { Dictionary, Locale } from '../i18n';
import { amount as money } from '../format';
import * as Icon from '../components/Icons';
import { ErrorAlert, failureOf, type Failure } from '../components/Alerts';
import { FiscalYearGap } from '../components/FiscalYearGap';
import type { Panel } from '../components/Shell';
import { fiscalYearLabel } from '../fiscalYear';

/**
 * Bilans i rachunek zysków i strat (E7-S3) — w dwóch układach z tych samych sald.
 *
 * Ekran niczego nie liczy — to backend rzutuje zestawienie obrotów i sald na tabele, a tu je
 * tylko widać. Gdyby liczyć po tej stronie, dałoby się doprowadzić do stanu, w którym
 * sprawozdanie mówi co innego niż księgi, a to najczęstsza usterka takich modułów.
 *
 * **Układ ustawowy** to pozycje załącznika 1, 4 albo 5 do ustawy o rachunkowości, z sald
 * zwiniętych po przypisaniu kont. Konto z saldem bez pozycji **nie znika**: dostaje czerwony
 * pasek na górze, bo sprawozdanie, które gubi konto po cichu, jest gorsze niż jego brak.
 * **Układ według kont** jest odniesieniem — każde saldo osobnym wierszem, nic nie może zginąć.
 */

type Layout = 'statutory' | 'accounts';

function currentYear(years: FiscalYear[]): FiscalYear | null {
  const today = new Date().toISOString().slice(0, 10);
  return years.find((year) => year.startsOn <= today && year.endsOn >= today) ?? years[0] ?? null;
}

/**
 * Jedna strona układu według kont: aktywa albo pasywa, przychody albo koszty.
 *
 * Wiersz `extra` to miejsce na wynik roku, który nie jest saldem żadnego konta, a mimo to stoi
 * w bilansie po stronie pasywów — bez niego suma nie zgadzałaby się z aktywami.
 */
function Side({
  title,
  lines,
  total,
  totalLabel,
  extra,
  t,
  locale,
  previous,
}: {
  title: string;
  lines: StatementLine[];
  total: string;
  totalLabel: string;
  extra?: { label: string; amount: string; previousAmount?: string | null };
  t: Dictionary;
  locale: Locale;
  /** Kolumna porównawcza: nagłówek roku i suma; brak, gdy to pierwszy rok firmy. */
  previous?: { label: string; total: string } | null;
}) {
  return (
    <div className="panel">
      <div className="hd">
        <div>
          <h4>{title}</h4>
        </div>
      </div>
      <div className="tbl">
        <table>
          <thead>
            <tr>
              <th>{t.reports.account}</th>
              <th className="wrap">{t.reports.name}</th>
              <th className="n">{previous ? t.reports.currentYear : t.reports.amount}</th>
              {previous && <th className="n">{previous.label}</th>}
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <tr key={line.account}>
                <td className="mono">{line.account}</td>
                <td className="wrap">{line.name}</td>
                <td className="n mono">{money(line.amount, locale)}</td>
                {previous && <td className="n mono muted">{line.previousAmount === null ? '' : money(line.previousAmount, locale)}</td>}
              </tr>
            ))}
            {extra && (
              <tr>
                <td />
                <td className="wrap">
                  <b>{extra.label}</b>
                </td>
                <td className="n mono">
                  <b>{money(extra.amount, locale)}</b>
                </td>
                {previous && <td className="n mono muted">{extra.previousAmount == null ? '' : money(extra.previousAmount, locale)}</td>}
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={2}>
                <b>{totalLabel}</b>
              </td>
              <td className="n mono">
                <b>{money(total, locale)}</b>
              </td>
              {previous && (
                <td className="n mono muted">
                  <b>{money(previous.total, locale)}</b>
                </td>
              )}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

/**
 * Tabela pozycji ustawowych jednej sekcji.
 *
 * Wcięcie niesie poziom, „w tym" jest wyszarzone, bo to część kwoty nadrzędnej, a nie jej
 * składnik; pozycje liczone formułą są wytłuszczone. Suma sekcji (poziom 0 bilansu) idzie
 * do stopki, a nagłówek wariantu RZiS staje się tytułem tabeli — sam kwoty nie niesie.
 */
function StatutoryTable({
  title,
  rows,
  t,
  locale,
  previousLabel,
}: {
  title: string;
  rows: StatutoryRow[];
  t: Dictionary;
  locale: Locale;
  /** Podpis kolumny porównawczej; brak, gdy poprzedni rok nie jest porównywalny. */
  previousLabel?: string | null;
}) {
  const header = rows.find((row) => row.header);
  const total = rows.find((row) => !row.header && row.level === 0 && !row.code.match(/^[A-Z]\./));
  const body = rows.filter((row) => row !== header && row !== total);

  return (
    <div className="panel">
      <div className="hd">
        <div>
          <h4>{header ? header.label : title}</h4>
        </div>
      </div>
      <div className="tbl">
        <table>
          <thead>
            <tr>
              <th className="wrap">{t.reports.position}</th>
              <th className="n">{previousLabel ? t.reports.currentYear : t.reports.amount}</th>
              {previousLabel && <th className="n">{previousLabel}</th>}
            </tr>
          </thead>
          <tbody>
            {body.map((row) => (
              <tr key={row.code} className={row.informational ? 'muted' : undefined}>
                <td className="wrap" style={{ paddingLeft: 12 + Math.max(0, row.level - (header ? 1 : 0)) * 18 }}>
                  {row.computed ? <b>{row.label}</b> : row.label}
                </td>
                <td className="n mono">
                  {row.amount === null ? '' : row.computed ? <b>{money(row.amount, locale)}</b> : money(row.amount, locale)}
                </td>
                {previousLabel && (
                  <td className="n mono muted">
                    {row.previousAmount === null ? '' : row.computed ? <b>{money(row.previousAmount, locale)}</b> : money(row.previousAmount, locale)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          {total && (
            <tfoot>
              <tr>
                <td>
                  <b>{total.label}</b>
                </td>
                <td className="n mono">
                  <b>{money(total.amount ?? '0', locale)}</b>
                </td>
                {previousLabel && (
                  <td className="n mono muted">
                    <b>{money(total.previousAmount ?? '0', locale)}</b>
                  </td>
                )}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

function Statutory({
  s,
  t,
  locale,
  onGo,
  previous,
}: {
  s: StatutoryStatement;
  t: Dictionary;
  locale: Locale;
  onGo: (panel: Panel) => void;
  previous: PreviousYear | null;
}) {
  const profit = Number(s.result) >= 0;
  const previousLabel = previous?.comparable ? previous.startsOn.slice(0, 4) : null;

  return (
    <>
      {/* Najważniejszy element ekranu: konta, których kwot w tabeli nie ma */}
      {s.unmapped.length > 0 && (
        <div className="alert a-bad">
          <Icon.Error />
          <div>
            <b>{t.reports.unmappedTitle(s.unmapped.length)}</b>
            <p>{t.reports.unmappedHint}</p>
            <ul className="plain">
              {s.unmapped.map((u) => (
                <li key={`${u.account}-${u.side}`}>
                  <span className="mono">{u.account}</span> {u.name} — {money(u.amount, locale)}{' '}
                  {u.side === 'DEBIT' ? t.journal.debit : t.journal.credit}
                </li>
              ))}
            </ul>
          </div>
          <button type="button" className="btn" onClick={() => onGo('chart')}>
            {t.reports.unmappedGoToChart}
          </button>
        </div>
      )}

      <div className="hd">
        <div>
          <h4>{t.reports.balanceSheet}</h4>
          <p>{t.reports.templates[s.template]}</p>
        </div>
        <div className="sp">
          <span className={s.balanced ? 'chip st-ok' : 'chip st-bad'}>
            {s.balanced ? t.reports.balanced : t.reports.notBalanced}
          </span>
        </div>
      </div>

      {previous && !previous.hasChart && <p className="note">{t.reports.previousNoChart}</p>}
      {previous && previous.hasChart && !previous.comparable && <p className="note">{t.reports.previousDifferentTemplate}</p>}

      <div className="grid two">
        <StatutoryTable title={t.reports.assets} rows={s.assets} t={t} locale={locale} previousLabel={previousLabel} />
        <StatutoryTable title={t.reports.liabilities} rows={s.liabilities} t={t} locale={locale} previousLabel={previousLabel} />
      </div>

      <div className="hd">
        <div>
          <h4>{t.reports.incomeStatement}</h4>
        </div>
        <div className="sp">
          <span className="chip">{(profit ? t.reports.profit : t.reports.loss) + ': ' + money(s.result, locale)}</span>
        </div>
      </div>
      <StatutoryTable title={t.reports.incomeStatement} rows={s.income} t={t} locale={locale} previousLabel={previousLabel} />

      <p className="note">{t.reports.computedNote}</p>
      <p className="note">{t.reports.settlementsNote}</p>
    </>
  );
}

export function ReportsPanel({
  company,
  t,
  locale,
  onGo,
}: {
  company: Company;
  t: Dictionary;
  locale: Locale;
  onGo: (panel: Panel) => void;
}) {
  const [years, setYears] = useState<FiscalYear[] | null>(null);
  const [yearId, setYearId] = useState<string | null>(null);
  /** Pusty znaczy „cały rok"; miesiąc zawęża sprawozdanie do stanu na jego koniec. */
  const [upTo, setUpTo] = useState('');
  const [layout, setLayout] = useState<Layout>('statutory');
  const [statements, setStatements] = useState<Statements | null>(null);
  const [error, setError] = useState<Failure | null>(null);

  useEffect(() => {
    api
      .fiscalYears(company.id)
      .then((loaded) => {
        setYears(loaded);
        setYearId((current) => current ?? currentYear(loaded)?.id ?? null);
      })
      .catch((failure) => setError(failureOf(failure)));
  }, [company.id]);

  const load = useCallback(async () => {
    if (!yearId) return;
    setError(null);
    try {
      setStatements(await api.statements(company.id, yearId, upTo || undefined));
    } catch (failure) {
      setStatements(null);
      setError(failureOf(failure));
    }
  }, [company.id, yearId, upTo]);

  useEffect(() => {
    void load();
  }, [load]);

  const sheet = statements?.balanceSheet;
  const income = statements?.incomeStatement;
  const statutory = statements?.statutory ?? null;
  // Kolumna porównawcza według kont: gdy poprzedni rok ma plan, konta łączą się po odpowiedniku
  const previousByAccounts =
    statements?.previous?.hasChart && statements.previousTotals
      ? { label: statements.previous.startsOn.slice(0, 4), ...statements.previousTotals }
      : null;
  // Wynik ujemny to strata i tak się nazywa; w tabeli zostaje ze znakiem, bo pasywa go pomniejszają
  const profit = income ? Number(income.result) >= 0 : true;
  const empty =
    sheet !== undefined &&
    sheet.assets.length === 0 &&
    sheet.liabilities.length === 0 &&
    income?.revenues.length === 0 &&
    income?.costs.length === 0;

  return (
    <>
      <div className="hd">
        <div>
          <h3>{t.reports.title}</h3>
          <p>{t.reports.lead}</p>
        </div>
        <div className="sp">
          <div className="seg" role="group" aria-label={t.reports.layout}>
            <button type="button" aria-pressed={layout === 'statutory'} onClick={() => setLayout('statutory')}>
              {t.reports.layoutStatutory}
            </button>
            <button type="button" aria-pressed={layout === 'accounts'} onClick={() => setLayout('accounts')}>
              {t.reports.layoutAccounts}
            </button>
          </div>
          <select
            className="inp w"
            aria-label={t.reports.year}
            value={yearId ?? ''}
            onChange={(event) => setYearId(event.target.value)}
          >
            {years?.map((item) => (
              <option key={item.id} value={item.id}>
                {fiscalYearLabel(item)}
              </option>
            ))}
          </select>
          <input
            className="inp w"
            type="month"
            aria-label={t.reports.month}
            value={upTo}
            onChange={(event) => setUpTo(event.target.value)}
          />
        </div>
      </div>

      <ErrorAlert error={error} t={t} onGo={onGo} />

      <FiscalYearGap
        companyId={company.id}
        roles={company.roles}
        years={years}
        t={t}
        onOpened={() => {
          void api.fiscalYears(company.id).then((loaded) => {
            setYears(loaded);
            setYearId(currentYear(loaded)?.id ?? null);
          });
        }}
      />

      {empty && (
        <div className="panel">
          <p>{t.reports.empty}</p>
          <div className="row">
            <button type="button" className="btn" onClick={() => onGo('journal')}>
              {t.reports.goToJournal}
            </button>
          </div>
        </div>
      )}

      {statements && !empty && layout === 'statutory' && (
        statutory ? (
          <Statutory s={statutory} t={t} locale={locale} onGo={onGo} previous={statements.previous} />
        ) : (
          <div className="panel">
            <p>{t.reports.noChart}</p>
            <div className="row">
              <button type="button" className="btn" onClick={() => onGo('chart')}>
                {t.chart.open}
              </button>
            </div>
          </div>
        )
      )}

      {sheet && income && !empty && layout === 'accounts' && (
        <>
          <div className="hd">
            <div>
              <h4>{t.reports.balanceSheet}</h4>
            </div>
            <div className="sp">
              <span className={sheet.balanced ? 'chip st-ok' : 'chip st-bad'}>
                {sheet.balanced ? t.reports.balanced : t.reports.notBalanced}
              </span>
            </div>
          </div>

          {/* Nierówność aktywów i pasywów znaczy, że do ksiąg weszło coś poza dziennikiem */}
          {!sheet.balanced && (
            <div className="alert a-bad">
              <Icon.Error />
              <div>
                <b>{t.reports.notBalancedHint}</b>
              </div>
              <button type="button" className="btn" onClick={() => onGo('journal')}>
                {t.reports.goToJournal}
              </button>
            </div>
          )}

          <div className="grid two">
            <Side
              title={t.reports.assets}
              lines={sheet.assets}
              total={sheet.totalAssets}
              totalLabel={t.reports.total}
              t={t}
              locale={locale}
              previous={previousByAccounts && { label: previousByAccounts.label, total: previousByAccounts.assets }}
            />
            <Side
              title={t.reports.liabilities}
              lines={sheet.liabilities}
              total={sheet.totalLiabilities}
              totalLabel={t.reports.total}
              extra={{ label: profit ? t.reports.profit : t.reports.loss, amount: sheet.result, previousAmount: previousByAccounts?.result }}
              t={t}
              locale={locale}
              previous={previousByAccounts && { label: previousByAccounts.label, total: previousByAccounts.liabilities }}
            />
          </div>
          <p className="note">{t.reports.resultNote}</p>

          <div className="hd">
            <div>
              <h4>{t.reports.incomeStatement}</h4>
            </div>
            <div className="sp">
              <span className="chip">
                {(profit ? t.reports.profit : t.reports.loss) + ': ' + money(income.result, locale)}
              </span>
            </div>
          </div>

          <div className="grid two">
            <Side
              title={t.reports.revenues}
              lines={income.revenues}
              total={income.totalRevenue}
              totalLabel={t.reports.total}
              t={t}
              locale={locale}
              previous={previousByAccounts && { label: previousByAccounts.label, total: previousByAccounts.revenue }}
            />
            <Side
              title={t.reports.costs}
              lines={income.costs}
              total={income.totalCost}
              totalLabel={t.reports.total}
              t={t}
              locale={locale}
              previous={previousByAccounts && { label: previousByAccounts.label, total: previousByAccounts.cost }}
            />
          </div>

          <p className="note">{t.reports.accountsNote}</p>
          <p className="note">{t.reports.settlementsNote}</p>
        </>
      )}
    </>
  );
}
